import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { 
  ClipboardList, Home, CheckCircle, Clock, AlertCircle, ClipboardCheck,
  MessageSquare, Send, LogOut, User, ChevronDown, ChevronUp,
  Calendar, MapPin, Printer, Navigation, Phone, Mail, ExternalLink,
  FileEdit, Plus, Users, BookOpen, Search, Video, Book, Mic, 
  Play, GraduationCap, Lightbulb, X, Bell, WifiOff, MapPinCheck, LogIn, LogOut as LogOutIcon
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isToday, addMonths, subMonths } from 'date-fns';
import { PhotoUpload } from '../components/PhotoUpload';
import { useOfflineSupport } from '../hooks/useOfflineSupport';
import TaskComments from '../components/TaskComments';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ElapsedTimer = ({ startTime }) => {
  const [elapsed, setElapsed] = React.useState('');
  
  React.useEffect(() => {
    const update = () => {
      const start = new Date(startTime);
      const now = new Date();
      const diffMs = now - start;
      const mins = Math.floor(diffMs / 60000);
      const hrs = Math.floor(mins / 60);
      const remainMins = mins % 60;
      
      if (hrs > 0) {
        setElapsed(`${hrs}h ${remainMins}m`);
      } else {
        setElapsed(`${mins}m`);
      }
    };
    update();
    const interval = setInterval(update, 30000); // update every 30s
    return () => clearInterval(interval);
  }, [startTime]);
  
  return (
    <span className="text-xs text-green-600 font-medium flex items-center gap-1" data-testid="elapsed-timer">
      <Clock className="h-3 w-3" />
      {elapsed} on site
    </span>
  );
};

const CrewDashboard = () => {
  const navigate = useNavigate();
  const { isOnline, queueLength, syncing } = useOfflineSupport();
  const [crewInfo, setCrewInfo] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [hives, setHives] = useState([]);
  const [yards, setYards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showTaskDetail, setShowTaskDetail] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [expandedTasks, setExpandedTasks] = useState({});
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [showMapDialog, setShowMapDialog] = useState(false);
  const printRef = useRef(null);
  
  // Inspection dialog state
  const [showInspectionDialog, setShowInspectionDialog] = useState(false);
  const [inspectionTask, setInspectionTask] = useState(null);
  const [inspectionData, setInspectionData] = useState({
    type: 'Quick Check',
    notes: '',
    queen_seen: '',
    brood_pattern: '',
    temperament: '',
    population: '',
    photos: []
  });

  // Knowledge section state
  const [showKnowledgeDialog, setShowKnowledgeDialog] = useState(false);
  const [knowledgeTab, setKnowledgeTab] = useState('resources');
  const [knowledgeResources, setKnowledgeResources] = useState([]);
  const [knowledgeArticles, setKnowledgeArticles] = useState([]);
  const [knowledgeGlossary, setKnowledgeGlossary] = useState([]);
  const [knowledgeLoading, setKnowledgeLoading] = useState(false);
  const [knowledgeSearch, setKnowledgeSearch] = useState('');
  const [aiQuestion, setAiQuestion] = useState('');
  const [aiAnswer, setAiAnswer] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [expandedArticle, setExpandedArticle] = useState(null);
  const [crewNotifications, setCrewNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [checkins, setCheckins] = useState([]);
  const [checkingIn, setCheckingIn] = useState(null); // yard_id being checked in
  const [lastInspections, setLastInspections] = useState({}); // hiveId -> date string

  // PWA Install prompt
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      const dismissed = localStorage.getItem('pwa-install-dismissed');
      if (!dismissed) setShowInstallBanner(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('crewToken');
    if (!token) {
      navigate('/crew-login');
      return;
    }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('crewToken');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [profileRes, tasksRes, hivesRes, yardsRes, notifRes, checkinsRes, inspRes] = await Promise.all([
        axios.get(`${API}/crew/me`, { headers }),
        axios.get(`${API}/crew/tasks`, { headers }),
        axios.get(`${API}/hives`, { headers }),
        axios.get(`${API}/yards`, { headers }),
        axios.get(`${API}/crew/notifications`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/crew/check-ins`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/inspections`, { headers }).catch(() => ({ data: [] }))
      ]);
      
      setCrewInfo(profileRes.data);
      setTasks(tasksRes.data || []);
      setHives(hivesRes.data || []);
      setYards(yardsRes.data || []);
      setCrewNotifications(notifRes.data || []);
      setCheckins(checkinsRes.data || []);
      
      // Build last inspection map: hiveId -> most recent date
      const inspMap = {};
      for (const insp of (inspRes.data || [])) {
        const hid = insp.hive_id;
        const d = insp.date;
        if (hid && d && (!inspMap[hid] || d > inspMap[hid])) {
          inspMap[hid] = d;
        }
      }
      setLastInspections(inspMap);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('crewToken');
        navigate('/crew-login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('crewToken');
    navigate('/crew-login');
    toast.success('Logged out successfully');
  };

  const handleStatusUpdate = async (taskId, newStatus) => {
    try {
      const token = localStorage.getItem('crewToken');
      const updateData = { status: newStatus };
      if (newStatus === 'completed') {
        updateData.completed_date = new Date().toISOString().split('T')[0];
      }
      
      await axios.put(`${API}/crew/tasks/${taskId}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Task marked as ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !selectedTask) return;
    
    try {
      const token = localStorage.getItem('crewToken');
      await axios.post(`${API}/crew/tasks/${selectedTask.id}/notes`, { message: newNote }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Note added');
      setNewNote('');
      
      // Refresh task data
      const tasksRes = await axios.get(`${API}/crew/tasks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTasks(tasksRes.data || []);
      
      // Update selected task
      const updatedTask = tasksRes.data.find(t => t.id === selectedTask.id);
      if (updatedTask) setSelectedTask(updatedTask);
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  // Open inspection dialog for a task
  const openInspectionDialog = (task) => {
    setInspectionTask(task);
    setInspectionData({
      type: 'Quick Check',
      notes: '',
      queen_seen: '',
      brood_pattern: '',
      temperament: '',
      population: '',
      photos: []
    });
    setShowInspectionDialog(true);
  };

  // Submit inspection for a task
  const handleSubmitInspection = async () => {
    if (!inspectionTask) return;
    
    try {
      const token = localStorage.getItem('crewToken');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Create inspection record
      const inspData = {
        hive_id: inspectionTask.hive_id,
        date: new Date().toISOString(),
        type_of_inspection: inspectionData.type,
        notes: `[Crew Inspection - ${crewInfo?.name}]\n${inspectionData.notes}`,
        queen_sighted: inspectionData.queen_seen === 'yes',
        brood_pattern: inspectionData.brood_pattern || undefined,
        temperament: inspectionData.temperament || undefined,
        population: inspectionData.population || undefined,
        crew_task_id: inspectionTask.id,
        photos: inspectionData.photos.map(p => p.path)
      };
      
      await axios.post(`${API}/inspections`, inspData, { headers });
      
      // Auto-complete the task if it's an inspection task
      if (inspectionTask.task_type === 'inspection' && inspectionTask.status !== 'completed') {
        await axios.put(`${API}/crew/tasks/${inspectionTask.id}`, {
          status: 'completed',
          completed_date: new Date().toISOString().split('T')[0]
        }, { headers });
      }
      
      toast.success('Inspection recorded successfully!');
      setShowInspectionDialog(false);
      setInspectionTask(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to record inspection');
    }
  };

  // Knowledge functions
  const fetchKnowledge = async () => {
    setKnowledgeLoading(true);
    try {
      const token = localStorage.getItem('crewToken');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [resourcesRes, articlesRes, glossaryRes] = await Promise.all([
        axios.get(`${API}/knowledge/resources`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/knowledge/articles`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/knowledge/glossary`, { headers }).catch(() => ({ data: [] }))
      ]);
      
      setKnowledgeResources(resourcesRes.data || []);
      setKnowledgeArticles(articlesRes.data || []);
      setKnowledgeGlossary(glossaryRes.data || []);
    } catch (error) {
    } finally {
      setKnowledgeLoading(false);
    }
  };

  const openKnowledgeDialog = () => {
    setShowKnowledgeDialog(true);
    if (knowledgeResources.length === 0) {
      fetchKnowledge();
    }
  };

  const askAI = async () => {
    if (!aiQuestion.trim()) return;
    
    setAiLoading(true);
    setAiAnswer('');
    try {
      const token = localStorage.getItem('crewToken');
      const res = await axios.post(`${API}/knowledge/ai-assistant/chat`, {
        message: aiQuestion,
        include_hive_context: false
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAiAnswer(res.data.response || 'No response received');
    } catch (error) {
      setAiAnswer('Sorry, I could not process your question. Please try again.');
    } finally {
      setAiLoading(false);
    }
  };

  const getResourceIcon = (type) => {
    switch (type) {
      case 'video': return <Video className="h-5 w-5 text-red-500" />;
      case 'book': return <Book className="h-5 w-5 text-blue-500" />;
      case 'podcast': return <Mic className="h-5 w-5 text-purple-500" />;
      case 'article': return <BookOpen className="h-5 w-5 text-green-500" />;
      default: return <ExternalLink className="h-5 w-5 text-stone-500" />;
    }
  };

  const filteredResources = knowledgeResources.filter(r => 
    !knowledgeSearch || 
    r.title?.toLowerCase().includes(knowledgeSearch.toLowerCase()) ||
    r.description?.toLowerCase().includes(knowledgeSearch.toLowerCase())
  );

  const filteredArticles = knowledgeArticles.filter(a =>
    !knowledgeSearch ||
    a.title?.toLowerCase().includes(knowledgeSearch.toLowerCase()) ||
    a.summary?.toLowerCase().includes(knowledgeSearch.toLowerCase())
  );

  const filteredGlossary = knowledgeGlossary.filter(g =>
    !knowledgeSearch ||
    g.term?.toLowerCase().includes(knowledgeSearch.toLowerCase()) ||
    g.definition?.toLowerCase().includes(knowledgeSearch.toLowerCase())
  );

  const toggleTaskExpand = (taskId) => {
    setExpandedTasks(prev => ({
      ...prev,
      [taskId]: !prev[taskId]
    }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-700 border-green-200';
      case 'in_progress': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'pending': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      default: return 'bg-stone-100 text-stone-700';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-500 text-white';
      case 'high': return 'bg-orange-500 text-white';
      case 'normal': return 'bg-blue-500 text-white';
      default: return 'bg-stone-400 text-white';
    }
  };

  // Calendar helpers
  const getTasksForDate = (date) => {
    return tasks.filter(task => {
      if (!task.due_date) return false;
      const taskDate = new Date(task.due_date);
      return isSameDay(taskDate, date);
    });
  };

  const renderCalendar = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
    const startDayOfWeek = monthStart.getDay();
    
    return (
      <div className="bg-white rounded-xl border border-stone-200 p-4">
        <div className="flex items-center justify-between mb-4">
          <Button variant="ghost" size="sm" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>
            ←
          </Button>
          <h3 className="font-semibold text-stone-800">
            {format(currentMonth, 'MMMM yyyy')}
          </h3>
          <Button variant="ghost" size="sm" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
            →
          </Button>
        </div>
        <div className="grid grid-cols-7 gap-1 text-center text-xs">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="font-medium text-stone-500 py-1">{day}</div>
          ))}
          {Array(startDayOfWeek).fill(null).map((_, i) => (
            <div key={`empty-${i}`} className="py-2"></div>
          ))}
          {days.map(day => {
            const dayTasks = getTasksForDate(day);
            const hasUrgent = dayTasks.some(t => t.priority === 'urgent');
            const hasTasks = dayTasks.length > 0;
            return (
              <div 
                key={day.toString()}
                className={`py-2 rounded-lg text-sm cursor-pointer transition-colors
                  ${isToday(day) ? 'bg-amber-500 text-white font-bold' : ''}
                  ${hasTasks && !isToday(day) ? 'bg-amber-100 font-medium' : ''}
                  ${hasUrgent && !isToday(day) ? 'bg-red-100 text-red-700' : ''}
                  hover:bg-amber-200
                `}
                title={dayTasks.map(t => t.title).join(', ')}
              >
                {format(day, 'd')}
                {hasTasks && <div className="w-1 h-1 rounded-full bg-amber-600 mx-auto mt-0.5"></div>}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Print function
  const handlePrint = () => {
    window.print();
  };

  // Get Google Maps directions URL
  const getDirectionsUrl = (yard) => {
    if (yard.latitude && yard.longitude) {
      return `https://www.google.com/maps/dir/?api=1&destination=${yard.latitude},${yard.longitude}`;
    }
    if (yard.address) {
      return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(yard.address)}`;
    }
    return null;
  };

  const getActiveCheckIn = (yardId) => {
    // Find the most recent check-in for this yard that doesn't have a corresponding check-out
    const yardCheckins = checkins.filter(c => c.yard_id === yardId);
    if (yardCheckins.length === 0) return null;
    const latest = yardCheckins[0]; // sorted by created_at desc
    if (latest.action === 'check_in') return latest;
    return null;
  };

  const handleCheckIn = async (yard, action = 'check_in') => {
    setCheckingIn(yard.id);
    try {
      const token = localStorage.getItem('crewToken');
      
      // Get GPS position
      let latitude = null, longitude = null, accuracy = null;
      try {
        const pos = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 60000
          });
        });
        latitude = pos.coords.latitude;
        longitude = pos.coords.longitude;
        accuracy = Math.round(pos.coords.accuracy);
      } catch (geoErr) {
        // GPS not available - still allow check-in without location
      }
      
      const res = await axios.post(`${API}/crew/check-in`, {
        yard_id: yard.id,
        yard_name: yard.name,
        latitude,
        longitude,
        accuracy,
        action
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      setCheckins(prev => [res.data, ...prev]);
      
      if (action === 'check_out' && res.data.duration_minutes) {
        const dur = res.data.duration_minutes;
        const hrs = Math.floor(dur / 60);
        const mins = Math.round(dur % 60);
        const timeStr = hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
        toast.success(`Checked out from ${yard.name} — ${timeStr} on site`);
      } else if (action === 'check_in') {
        toast.success(`Checked in at ${yard.name}${accuracy ? ` (${accuracy}m accuracy)` : ''}`);
      } else {
        toast.success(`Checked out from ${yard.name}`);
      }
    } catch (err) {
      toast.error('Check-in failed');
    } finally {
      setCheckingIn(null);
    }
  };

  const pendingTasks = tasks.filter(t => t.status === 'pending');
  const inProgressTasks = tasks.filter(t => t.status === 'in_progress');
  const completedTasks = tasks.filter(t => t.status === 'completed');

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
      {/* Offline Banner */}
      {!isOnline && (
        <div className="bg-stone-800 text-white px-4 py-2 text-center text-sm flex items-center justify-center gap-2" data-testid="offline-banner">
          <WifiOff className="h-4 w-4 text-red-400" />
          You're offline — viewing cached data. Changes will sync when you're back online.
          {queueLength > 0 && <span className="bg-white/20 px-2 py-0.5 rounded text-xs">{queueLength} pending</span>}
        </div>
      )}
      {syncing && (
        <div className="bg-blue-600 text-white px-4 py-1.5 text-center text-xs" data-testid="syncing-banner">
          Syncing {queueLength} queued action{queueLength !== 1 ? 's' : ''}...
        </div>
      )}
      {/* Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 font-bold">
              {crewInfo?.name?.charAt(0)?.toUpperCase() || 'C'}
            </div>
            <div>
              <div className="font-semibold text-stone-800">{crewInfo?.name}</div>
              <div className="text-xs text-stone-500">Crew Member</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Notification Bell */}
            <div className="relative">
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative p-2 rounded-full hover:bg-stone-100 transition-colors"
                data-testid="crew-notification-bell"
              >
                <Bell className="h-5 w-5 text-stone-600" />
                {crewNotifications.filter(n => !n.read).length > 0 && (
                  <span className="absolute top-0.5 right-0.5 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                    {crewNotifications.filter(n => !n.read).length}
                  </span>
                )}
              </button>
              {showNotifications && (
                <div className="absolute right-0 top-full mt-1 w-80 bg-white rounded-xl border border-stone-200 shadow-lg z-50 max-h-80 overflow-y-auto" data-testid="crew-notification-dropdown">
                  <div className="p-3 border-b border-stone-100 font-semibold text-sm text-stone-700">Notifications</div>
                  {crewNotifications.length === 0 ? (
                    <div className="p-4 text-center text-sm text-stone-400">No notifications</div>
                  ) : (
                    crewNotifications.slice(0, 20).map(n => (
                      <div key={n.id} className={`p-3 border-b border-stone-50 text-sm ${n.read ? 'opacity-60' : 'bg-amber-50/50'}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <div className="font-medium text-stone-800">{n.title}</div>
                            <div className="text-stone-500 text-xs mt-0.5">{n.message}</div>
                            <div className="text-stone-400 text-[10px] mt-1">
                              {new Date(n.created_at).toLocaleString()}
                            </div>
                          </div>
                          {!n.read && (
                            <button
                              onClick={async () => {
                                const token = localStorage.getItem('crewToken');
                                await axios.put(`${API}/crew/notifications/${n.id}/read`, {}, {
                                  headers: { Authorization: `Bearer ${token}` }
                                }).catch(() => {});
                                setCrewNotifications(prev => prev.map(notif =>
                                  notif.id === n.id ? { ...notif, read: true } : notif
                                ));
                              }}
                              className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 hover:bg-amber-200 whitespace-nowrap flex-shrink-0"
                            >
                              Mark read
                            </button>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
            <Button variant="ghost" onClick={handleLogout} className="text-stone-600">
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8 print:p-2" ref={printRef}>
        {/* Action Bar */}
        <div className="flex items-center justify-between mb-6 print:hidden">
          <h1 className="text-2xl font-bold text-stone-800">My Dashboard</h1>
          <div className="flex gap-2">
            <Button 
              variant="outline"
              onClick={() => navigate('/team-chat')}
              className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200 text-amber-700 hover:from-amber-100 hover:to-orange-100"
              data-testid="crew-team-chat-btn"
            >
              <MessageSquare className="h-4 w-4 mr-2" />
              Team Chat
            </Button>
            <Button 
              variant="outline" 
              onClick={openKnowledgeDialog}
              className="bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200 text-emerald-700 hover:from-emerald-100 hover:to-teal-100"
            >
              <GraduationCap className="h-4 w-4 mr-2" />
              Knowledge
            </Button>
            <Button variant="outline" onClick={() => setShowMapDialog(true)}>
              <MapPin className="h-4 w-4 mr-2" />
              Yard Map
            </Button>
            <Button variant="outline" onClick={handlePrint}>
              <Printer className="h-4 w-4 mr-2" />
              Print
            </Button>
          </div>
        </div>

        {/* Print Header - only shows when printing */}
        <div className="hidden print:block mb-4">
          <h1 className="text-xl font-bold">Task List for {crewInfo?.name}</h1>
          <p className="text-sm text-stone-600">Printed: {format(new Date(), 'PPpp')}</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8 print:grid-cols-3 print:gap-2">
          <div className="bg-white rounded-xl border border-stone-200 p-4 text-center">
            <div className="text-3xl font-bold text-yellow-600">{pendingTasks.length}</div>
            <div className="text-sm text-stone-600">Pending</div>
          </div>
          <div className="bg-white rounded-xl border border-stone-200 p-4 text-center">
            <div className="text-3xl font-bold text-blue-600">{inProgressTasks.length}</div>
            <div className="text-sm text-stone-600">In Progress</div>
          </div>
          <div className="bg-white rounded-xl border border-stone-200 p-4 text-center">
            <div className="text-3xl font-bold text-green-600">{completedTasks.length}</div>
            <div className="text-sm text-stone-600">Completed</div>
          </div>
        </div>

        {/* Calendar and Quick Info */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8 print:hidden">
          {/* Calendar */}
          <div className="lg:col-span-2">
            {renderCalendar()}
          </div>
          
          {/* Yards Quick View */}
          <div className="bg-white rounded-xl border border-stone-200 p-4">
            <h3 className="font-semibold text-stone-800 flex items-center gap-2 mb-3">
              <MapPin className="h-4 w-4 text-amber-600" />
              Assigned Yards ({yards.length})
            </h3>
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {yards.slice(0, 5).map(yard => {
                const activeCheckIn = getActiveCheckIn(yard.id);
                return (
                  <div key={yard.id} className={`p-2 rounded-lg text-sm ${activeCheckIn ? 'bg-green-50 border border-green-200' : 'bg-stone-50'}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-stone-800 flex items-center gap-1.5">
                          {yard.name}
                          {activeCheckIn && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-green-500 text-white rounded-full font-bold" data-testid={`checked-in-badge-${yard.id}`}>
                              ON SITE
                            </span>
                          )}
                        </div>
                        {yard.address && (
                          <div className="text-xs text-stone-500 truncate">{yard.address}</div>
                        )}
                      </div>
                      {/* Check In / Out Button */}
                      <button
                        onClick={() => handleCheckIn(yard, activeCheckIn ? 'check_out' : 'check_in')}
                        disabled={checkingIn === yard.id}
                        className={`flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md transition-colors flex-shrink-0 ml-2 ${
                          activeCheckIn
                            ? 'bg-red-50 text-red-700 hover:bg-red-100'
                            : 'bg-green-50 text-green-700 hover:bg-green-100'
                        } ${checkingIn === yard.id ? 'opacity-50' : ''}`}
                        data-testid={`check-in-btn-${yard.id}`}
                      >
                        {checkingIn === yard.id ? (
                          <Clock className="h-3 w-3 animate-spin" />
                        ) : activeCheckIn ? (
                          <LogOutIcon className="h-3 w-3" />
                        ) : (
                          <LogIn className="h-3 w-3" />
                        )}
                        {activeCheckIn ? 'Out' : 'In'}
                      </button>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      {getDirectionsUrl(yard) && (
                        <a 
                          href={getDirectionsUrl(yard)} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                        >
                          <Navigation className="h-3 w-3" /> Directions
                        </a>
                      )}
                      {activeCheckIn && (
                        <ElapsedTimer startTime={activeCheckIn.created_at} />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Tasks */}
        <div className="bg-white rounded-2xl border border-stone-200 shadow-sm print:shadow-none print:border">
          <div className="p-6 border-b border-stone-100">
            <h2 className="text-xl font-bold text-stone-800 flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-amber-600" />
              My Tasks
            </h2>
          </div>

          <div className="divide-y divide-stone-100">
            {tasks.length === 0 ? (
              <div className="p-8 text-center text-stone-500">
                <ClipboardList className="h-12 w-12 mx-auto mb-3 text-stone-300" />
                <p>No tasks assigned yet</p>
              </div>
            ) : (
              tasks.map(task => {
                const isExpanded = expandedTasks[task.id];
                const hive = hives.find(h => h.id === task.hive_id);
                
                return (
                  <div key={task.id} className="hover:bg-stone-50">
                    <button
                      onClick={() => toggleTaskExpand(task.id)}
                      className="w-full p-4 text-left flex items-center justify-between"
                    >
                      <div className="flex items-center gap-4 flex-1">
                        <span className={`px-2 py-1 text-xs rounded-full ${getPriorityColor(task.priority)}`}>
                          {task.priority}
                        </span>
                        <div className="flex-1">
                          <div className="font-medium text-stone-800 flex items-center gap-2">
                            {task.title}
                            {(() => {
                              const created = new Date(task.created_at);
                              const now = new Date();
                              const hoursDiff = (now - created) / (1000 * 60 * 60);
                              return hoursDiff < 24 ? (
                                <span className="px-1.5 py-0.5 text-[10px] font-bold bg-red-500 text-white rounded-full animate-pulse" data-testid="new-task-badge">NEW</span>
                              ) : null;
                            })()}
                          </div>
                          <div className="text-sm text-stone-500 flex items-center gap-2 mt-1 flex-wrap">
                            {/* Apiary/Yard Info */}
                            {task.yard && (
                              <span className="flex items-center gap-1 text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded">
                                <MapPin className="h-3.5 w-3.5" />
                                {task.yard}
                              </span>
                            )}
                            {task.hive_number > 0 && (
                              <span className="flex items-center gap-1">
                                <Home className="h-3.5 w-3.5" />
                                Hive {task.hive_number}
                              </span>
                            )}
                            {task.hive_id && lastInspections[task.hive_id] && (
                              <span className="flex items-center gap-1 text-xs text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded" data-testid={`last-inspection-${task.id}`}>
                                <ClipboardCheck className="h-3 w-3" />
                                Inspected {format(new Date(lastInspections[task.hive_id]), 'MMM d')}
                              </span>
                            )}
                            {task.task_type && (
                              <span className="bg-stone-100 px-2 py-0.5 rounded text-xs">
                                {task.task_type}
                              </span>
                            )}
                            {task.due_date && (
                              <span className="text-xs">
                                Due: {format(new Date(task.due_date), 'MMM d')}
                              </span>
                            )}
                            {/* Show other assigned crew members */}
                            {task.assigned_crew && task.assigned_crew.length > 1 && (
                              <span className="flex items-center gap-1 text-xs text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded">
                                <Users className="h-3 w-3" />
                                +{task.assigned_crew.length - 1} other{task.assigned_crew.length > 2 ? 's' : ''}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`px-2 py-1 text-xs rounded-full border ${getStatusColor(task.status)}`}>
                          {task.status.replace('_', ' ')}
                        </span>
                        {task.notes?.length > 0 && (
                          <span className="flex items-center gap-1 text-xs text-stone-500">
                            <MessageSquare className="h-3.5 w-3.5" />
                            {task.notes.length}
                          </span>
                        )}
                        {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </div>
                    </button>
                    
                    {isExpanded && (
                      <div className="px-4 pb-4 bg-stone-50">
                        {/* Other Assigned Crew */}
                        {task.assigned_crew && task.assigned_crew.length > 1 && (
                          <div className="mb-4 p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                            <div className="text-xs font-medium text-indigo-700 mb-2 flex items-center gap-1">
                              <Users className="h-3.5 w-3.5" />
                              Also Assigned To This Task
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {task.assigned_crew
                                .filter(c => c.id !== crewInfo?.id)
                                .map(c => (
                                  <span key={c.id} className="bg-white px-2 py-1 rounded text-sm text-indigo-700 border border-indigo-200">
                                    {c.name}
                                  </span>
                                ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Description */}
                        {task.description && (
                          <div className="mb-4 p-3 bg-white rounded-lg border border-stone-200">
                            <div className="text-xs font-medium text-stone-500 mb-1">Description</div>
                            <p className="text-sm text-stone-700">{task.description}</p>
                          </div>
                        )}
                        
                        {/* Hive Info */}
                        {hive && (
                          <div className="mb-4 p-3 bg-white rounded-lg border border-stone-200">
                            <div className="text-xs font-medium text-stone-500 mb-1">Hive Details</div>
                            <p className="text-sm text-stone-700">
                              Hive {hive.hive_number} {hive.nickname && `"${hive.nickname}"`}
                              {hive.yard && ` • ${hive.yard}`}
                            </p>
                          </div>
                        )}
                        
                        {/* Status Actions */}
                        <div className="flex flex-wrap gap-2 mb-4">
                          {task.status === 'pending' && (
                            <Button
                              size="sm"
                              onClick={() => handleStatusUpdate(task.id, 'in_progress')}
                              className="bg-blue-500 hover:bg-blue-600"
                            >
                              <Clock className="h-4 w-4 mr-1" />
                              Start Task
                            </Button>
                          )}
                          {task.status === 'in_progress' && (
                            <Button
                              size="sm"
                              onClick={() => handleStatusUpdate(task.id, 'completed')}
                              className="bg-green-500 hover:bg-green-600"
                            >
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Mark Complete
                            </Button>
                          )}
                          {task.status !== 'pending' && task.status !== 'completed' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleStatusUpdate(task.id, 'pending')}
                            >
                              Reset to Pending
                            </Button>
                          )}
                          
                          {/* Record Inspection Button - navigates to full inspection form */}
                          {task.hive_id && task.status !== 'completed' && (
                            <Button
                              size="sm"
                              onClick={() => navigate(`/?view=new-inspection&hiveId=${task.hive_id}`)}
                              className="bg-amber-500 hover:bg-amber-600 text-black"
                              data-testid={`record-inspection-${task.id}`}
                            >
                              <FileEdit className="h-4 w-4 mr-1" />
                              Record Inspection
                            </Button>
                          )}
                        </div>
                        
                        {/* Comments Thread */}
                        <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
                          <TaskComments taskId={task.id} taskTitle={task.title} />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Yard Map Dialog */}
      <Dialog open={showMapDialog} onOpenChange={setShowMapDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-amber-600" />
              Yard Locations & Contact Info
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex gap-2 mb-4">
            <Button 
              variant="outline" 
              onClick={() => {
                const printContent = document.getElementById('yard-list-print');
                if (printContent) {
                  const printWindow = window.open('', '', 'width=800,height=600');
                  printWindow.document.write(`
                    <html>
                      <head><title>Yard List - ${crewInfo?.name}</title>
                      <style>
                        body { font-family: Arial, sans-serif; padding: 20px; }
                        .yard { border: 1px solid #ddd; padding: 15px; margin: 10px 0; border-radius: 8px; }
                        .yard-name { font-size: 18px; font-weight: bold; margin-bottom: 8px; }
                        .info { margin: 4px 0; color: #666; }
                      </style>
                      </head>
                      <body>
                        <h1>Yard Locations</h1>
                        <p>Crew Member: ${crewInfo?.name} | Printed: ${new Date().toLocaleString()}</p>
                        ${printContent.innerHTML}
                      </body>
                    </html>
                  `);
                  printWindow.document.close();
                  printWindow.print();
                }
              }}
            >
              <Printer className="h-4 w-4 mr-2" />
              Print List
            </Button>
          </div>
          
          <div id="yard-list-print" className="space-y-4 max-h-[60vh] overflow-y-auto">
            {yards.map(yard => (
              <div key={yard.id} className="yard bg-stone-50 border border-stone-200 rounded-xl p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="yard-name text-lg font-bold text-stone-800">{yard.name}</h3>
                    
                    {yard.address && (
                      <div className="info flex items-center gap-2 text-stone-600 mt-2">
                        <MapPin className="h-4 w-4 text-stone-400" />
                        <span>{yard.address}</span>
                      </div>
                    )}
                    
                    {yard.contact_name && (
                      <div className="info flex items-center gap-2 text-stone-600 mt-1">
                        <User className="h-4 w-4 text-stone-400" />
                        <span>{yard.contact_name}</span>
                      </div>
                    )}
                    
                    {yard.contact_phone && (
                      <div className="info flex items-center gap-2 text-stone-600 mt-1">
                        <Phone className="h-4 w-4 text-stone-400" />
                        <a href={`tel:${yard.contact_phone}`} className="text-blue-600 hover:underline">
                          {yard.contact_phone}
                        </a>
                      </div>
                    )}
                    
                    {yard.contact_email && (
                      <div className="info flex items-center gap-2 text-stone-600 mt-1">
                        <Mail className="h-4 w-4 text-stone-400" />
                        <a href={`mailto:${yard.contact_email}`} className="text-blue-600 hover:underline">
                          {yard.contact_email}
                        </a>
                      </div>
                    )}
                    
                    {yard.notes && (
                      <div className="mt-2 text-sm text-stone-500 bg-white p-2 rounded">
                        <span className="font-medium">Notes:</span> {yard.notes}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex flex-col gap-2 ml-4">
                    {getDirectionsUrl(yard) && (
                      <a 
                        href={getDirectionsUrl(yard)} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-3 py-2 rounded-lg text-sm"
                      >
                        <Navigation className="h-4 w-4" />
                        Directions
                      </a>
                    )}
                    
                    {(yard.latitude && yard.longitude) && (
                      <a 
                        href={`https://www.google.com/maps?q=${yard.latitude},${yard.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-3 py-2 rounded-lg text-sm"
                      >
                        <ExternalLink className="h-4 w-4" />
                        View Map
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
            
            {yards.length === 0 && (
              <div className="text-center py-8 text-stone-500">
                <MapPin className="h-12 w-12 mx-auto mb-3 text-stone-300" />
                <p>No yards assigned</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* PWA Install Banner */}
      {showInstallBanner && installPrompt && (
        <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-80 bg-white rounded-xl border border-amber-200 shadow-xl p-4 z-50" data-testid="pwa-install-banner">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
              <Home className="h-5 w-5 text-amber-700" />
            </div>
            <div className="flex-1">
              <div className="font-semibold text-stone-800 text-sm">Install Thrive App</div>
              <div className="text-xs text-stone-500 mt-0.5">Quick access from your home screen with offline support</div>
              <div className="flex gap-2 mt-2">
                <button
                  onClick={async () => {
                    installPrompt.prompt();
                    const result = await installPrompt.userChoice;
                    if (result.outcome === 'accepted') {
                      setShowInstallBanner(false);
                    }
                  }}
                  className="px-3 py-1 text-xs font-medium bg-amber-600 text-white rounded-md hover:bg-amber-700"
                  data-testid="pwa-install-btn"
                >
                  Install
                </button>
                <button
                  onClick={() => {
                    setShowInstallBanner(false);
                    localStorage.setItem('pwa-install-dismissed', 'true');
                  }}
                  className="px-3 py-1 text-xs text-stone-500 hover:text-stone-700"
                >
                  Not now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Inspection Dialog */}
      <Dialog open={showInspectionDialog} onOpenChange={setShowInspectionDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileEdit className="h-5 w-5 text-amber-600" />
              Record Inspection - Hive #{inspectionTask?.hive_number}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              Recording inspection for task: <strong>{inspectionTask?.title}</strong>
            </div>
            
            <div className="space-y-2">
              <Label>Inspection Type</Label>
              <Select 
                value={inspectionData.type} 
                onValueChange={(v) => setInspectionData({...inspectionData, type: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Quick Check">Quick Check</SelectItem>
                  <SelectItem value="Full Inspection">Full Inspection</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Queen Sighted?</Label>
              <Select 
                value={inspectionData.queen_seen} 
                onValueChange={(v) => setInspectionData({...inspectionData, queen_seen: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                  <SelectItem value="not_checked">Not Checked</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Brood Pattern</Label>
                <Select 
                  value={inspectionData.brood_pattern} 
                  onValueChange={(v) => setInspectionData({...inspectionData, brood_pattern: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Excellent">Excellent</SelectItem>
                    <SelectItem value="Good">Good</SelectItem>
                    <SelectItem value="Average">Average</SelectItem>
                    <SelectItem value="Spotty">Spotty</SelectItem>
                    <SelectItem value="Poor">Poor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Temperament</Label>
                <Select 
                  value={inspectionData.temperament} 
                  onValueChange={(v) => setInspectionData({...inspectionData, temperament: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Calm">Calm</SelectItem>
                    <SelectItem value="Nervous">Nervous</SelectItem>
                    <SelectItem value="Aggressive">Aggressive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Population</Label>
              <Select 
                value={inspectionData.population} 
                onValueChange={(v) => setInspectionData({...inspectionData, population: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Strong">Strong</SelectItem>
                  <SelectItem value="Moderate">Moderate</SelectItem>
                  <SelectItem value="Weak">Weak</SelectItem>
                  <SelectItem value="Very Weak">Very Weak</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Photos</Label>
              <PhotoUpload
                photos={inspectionData.photos}
                onPhotosChange={(photos) => setInspectionData({...inspectionData, photos})}
                maxPhotos={5}
              />
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                placeholder="Enter inspection notes..."
                value={inspectionData.notes}
                onChange={(e) => setInspectionData({...inspectionData, notes: e.target.value})}
                rows={4}
              />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowInspectionDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSubmitInspection}
                className="bg-amber-500 hover:bg-amber-600 text-black"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Save Inspection
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Knowledge Library Dialog */}
      <Dialog open={showKnowledgeDialog} onOpenChange={setShowKnowledgeDialog}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GraduationCap className="h-6 w-6 text-emerald-600" />
              Knowledge Library
              <span className="text-sm font-normal text-stone-500 ml-2">Learn & Grow</span>
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden flex flex-col">
            {/* Tabs */}
            <div className="flex gap-2 mb-4 border-b border-stone-200 pb-3">
              {[
                { id: 'resources', label: 'Resources', icon: Video },
                { id: 'articles', label: 'Articles', icon: BookOpen },
                { id: 'glossary', label: 'Glossary', icon: Book },
                { id: 'ask-ai', label: 'Ask AI', icon: Lightbulb }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setKnowledgeTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    knowledgeTab === tab.id 
                      ? 'bg-emerald-100 text-emerald-700' 
                      : 'text-stone-600 hover:bg-stone-100'
                  }`}
                >
                  <tab.icon className="h-4 w-4" />
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Search Bar */}
            {knowledgeTab !== 'ask-ai' && (
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                <Input
                  placeholder="Search..."
                  value={knowledgeSearch}
                  onChange={(e) => setKnowledgeSearch(e.target.value)}
                  className="pl-10"
                />
                {knowledgeSearch && (
                  <button 
                    onClick={() => setKnowledgeSearch('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            )}

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              {knowledgeLoading ? (
                <div className="flex items-center justify-center h-40">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
                </div>
              ) : (
                <>
                  {/* Resources Tab */}
                  {knowledgeTab === 'resources' && (
                    <div className="space-y-3">
                      {filteredResources.length === 0 ? (
                        <div className="text-center py-8 text-stone-500">
                          <Video className="h-12 w-12 mx-auto mb-3 opacity-30" />
                          <p>No resources available yet.</p>
                          <p className="text-sm">Check back later for videos, podcasts, and more!</p>
                        </div>
                      ) : (
                        filteredResources.map(resource => (
                          <a
                            key={resource.id}
                            href={resource.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-start gap-4 p-4 bg-stone-50 rounded-lg hover:bg-stone-100 transition-colors"
                          >
                            {resource.thumbnail_url ? (
                              <img 
                                src={resource.thumbnail_url} 
                                alt={resource.title} 
                                className="w-24 h-16 object-cover rounded"
                              />
                            ) : (
                              <div className="w-24 h-16 bg-stone-200 rounded flex items-center justify-center">
                                {getResourceIcon(resource.resource_type)}
                              </div>
                            )}
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                {getResourceIcon(resource.resource_type)}
                                <h4 className="font-medium text-stone-800">{resource.title}</h4>
                              </div>
                              {resource.description && (
                                <p className="text-sm text-stone-600 mt-1 line-clamp-2">{resource.description}</p>
                              )}
                              {resource.platform && (
                                <span className="text-xs text-stone-500 mt-1 inline-block">{resource.platform}</span>
                              )}
                            </div>
                            <ExternalLink className="h-4 w-4 text-stone-400 flex-shrink-0" />
                          </a>
                        ))
                      )}
                    </div>
                  )}

                  {/* Articles Tab */}
                  {knowledgeTab === 'articles' && (
                    <div className="space-y-3">
                      {filteredArticles.length === 0 ? (
                        <div className="text-center py-8 text-stone-500">
                          <BookOpen className="h-12 w-12 mx-auto mb-3 opacity-30" />
                          <p>No articles available yet.</p>
                          <p className="text-sm">Learn beekeeping fundamentals soon!</p>
                        </div>
                      ) : (
                        filteredArticles.map(article => (
                          <div 
                            key={article.id}
                            className="bg-stone-50 rounded-lg overflow-hidden"
                          >
                            <button
                              onClick={() => setExpandedArticle(expandedArticle === article.id ? null : article.id)}
                              className="w-full p-4 flex items-center justify-between text-left hover:bg-stone-100"
                            >
                              <div>
                                <h4 className="font-medium text-stone-800">{article.title}</h4>
                                <p className="text-sm text-stone-600 mt-1">{article.summary}</p>
                              </div>
                              {expandedArticle === article.id ? (
                                <ChevronUp className="h-5 w-5 text-stone-400" />
                              ) : (
                                <ChevronDown className="h-5 w-5 text-stone-400" />
                              )}
                            </button>
                            {expandedArticle === article.id && article.content && (
                              <div className="px-4 pb-4 border-t border-stone-200">
                                <div className="prose prose-sm max-w-none mt-3 text-stone-700">
                                  {article.content.split('\n').map((para, i) => (
                                    <p key={i} className="mb-2">{para}</p>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  )}

                  {/* Glossary Tab */}
                  {knowledgeTab === 'glossary' && (
                    <div className="space-y-2">
                      {filteredGlossary.length === 0 ? (
                        <div className="text-center py-8 text-stone-500">
                          <Book className="h-12 w-12 mx-auto mb-3 opacity-30" />
                          <p>No glossary terms available yet.</p>
                          <p className="text-sm">Beekeeping terminology coming soon!</p>
                        </div>
                      ) : (
                        filteredGlossary.map(term => (
                          <div key={term.id} className="p-3 bg-stone-50 rounded-lg">
                            <h4 className="font-medium text-emerald-700">{term.term}</h4>
                            <p className="text-sm text-stone-600 mt-1">{term.definition}</p>
                          </div>
                        ))
                      )}
                    </div>
                  )}

                  {/* Ask AI Tab */}
                  {knowledgeTab === 'ask-ai' && (
                    <div className="space-y-4">
                      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <Lightbulb className="h-5 w-5 text-emerald-600 mt-0.5" />
                          <div>
                            <h4 className="font-medium text-emerald-800">Ask the Beekeeping AI</h4>
                            <p className="text-sm text-emerald-700 mt-1">
                              Have a question about beekeeping? Ask anything about hive management, 
                              bee behavior, diseases, seasonal care, or equipment.
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex gap-2">
                        <Input
                          placeholder="e.g., What are signs of a queenless hive?"
                          value={aiQuestion}
                          onChange={(e) => setAiQuestion(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && !aiLoading && askAI()}
                          disabled={aiLoading}
                        />
                        <Button 
                          onClick={askAI} 
                          disabled={aiLoading || !aiQuestion.trim()}
                          className="bg-emerald-600 hover:bg-emerald-700"
                        >
                          {aiLoading ? (
                            <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                          ) : (
                            <Send className="h-4 w-4" />
                          )}
                        </Button>
                      </div>

                      {aiAnswer && (
                        <div className="bg-white border border-stone-200 rounded-lg p-4">
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                              <Lightbulb className="h-4 w-4 text-emerald-600" />
                            </div>
                            <div className="flex-1 text-stone-700 prose prose-sm max-w-none">
                              {aiAnswer.split('\n').map((para, i) => (
                                <p key={i} className="mb-2">{para}</p>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="text-xs text-stone-500 text-center">
                        AI responses are for educational purposes. Always consult experienced beekeepers for critical decisions.
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CrewDashboard;
